import { Image } from 'expo-image';
import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
  View,
} from 'react-native';

import { tokens } from '../../tokens';
import { IconEdit } from '../icon';
import { Pressable } from '../pressable';
import { ScrollView, type ScrollViewProps } from '../scroll-view';
import { Text } from '../text';

export type StepProps = {
  isActive?: boolean;
  image?: string;
  disabled?: boolean;
};

export type ItemStepperProps = ScrollViewProps & {
  steps: StepProps[];
  onStepClick?: (index: number) => void;
};

type StepBubbleProps = {
  step: DisplayStep;
  isLast: boolean;
  size: number;
  onStepClick?: (index: number) => void;
};

type DisplayStep = StepProps & {
  countLabel?: string;
  isCount?: boolean;
  key: string;
  stepIndex?: number;
};

const MIN_VISIBLE_STEPS = 4;
const MAX_VISIBLE_STEPS = 6;
const DIVIDER_SIZE = 8;
const CONTENT_PADDING = 8;
const DEFAULT_BUBBLE_SIZE = 36;
const MAX_BUBBLE_SIZE = 42;
const MIN_BUBBLE_SIZE = 28;
const THRESHOLD_BUBBLE_SIZE = 34;

const getMaxVisibleSteps = (containerWidth: number): number => {
  if (containerWidth <= 0) {
    return MIN_VISIBLE_STEPS;
  }
  const stepSlot = THRESHOLD_BUBBLE_SIZE + DIVIDER_SIZE;
  const availableWidth = containerWidth - CONTENT_PADDING + DIVIDER_SIZE;
  return Math.min(
    MAX_VISIBLE_STEPS,
    Math.max(MIN_VISIBLE_STEPS, Math.floor(availableWidth / stepSlot))
  );
};

const buildDisplaySteps = (steps: StepProps[], maxVisibleSteps: number): DisplayStep[] => {
  const mapSteps = (items: StepProps[], startIndex = 0): DisplayStep[] =>
    items.map((step, index) => {
      const stepIndex = startIndex + index;
      return {
        ...step,
        key: `step-${stepIndex}`,
        stepIndex,
      };
    });
  const countStep = (count: number, stepIndex: number | undefined, key: string): DisplayStep => ({
    isCount: true,
    countLabel: count > 0 ? `+${count}` : '0',
    stepIndex,
    key,
  });

  if (steps.length <= maxVisibleSteps) {
    return mapSteps(steps);
  }

  const middleVisibleSteps = Math.max(2, maxVisibleSteps - 2);
  const activeIndex = Math.max(
    0,
    steps.findIndex(step => step.isActive)
  );

  // Near the start: show first N steps + trailing "+N remaining" count
  if (activeIndex <= middleVisibleSteps - 1) {
    const visibleCount = maxVisibleSteps - 1;
    const remainingCount = steps.length - visibleCount;
    return [
      ...mapSteps(steps.slice(0, visibleCount)),
      countStep(
        remainingCount,
        remainingCount > 0 ? visibleCount : undefined,
        'count-remaining'
      ),
    ];
  }

  // Near the end: show leading "+N previous" count + last N steps
  if (activeIndex >= steps.length - middleVisibleSteps) {
    const windowStart = Math.max(0, steps.length - (maxVisibleSteps - 1));
    const previousCount = windowStart;
    return [
      countStep(
        previousCount,
        previousCount > 0 ? previousCount - 1 : undefined,
        'count-previous'
      ),
      ...mapSteps(steps.slice(windowStart), windowStart),
    ];
  }

  // Middle: show "+N previous" + centered window + "+N remaining"
  const maxWindowStart = Math.max(0, steps.length - middleVisibleSteps);
  const windowStart = Math.min(
    Math.max(activeIndex - Math.floor(middleVisibleSteps / 2), 0),
    maxWindowStart
  );
  const windowSteps = steps.slice(windowStart, windowStart + middleVisibleSteps);
  const previousCount = windowStart;
  const remainingCount = steps.length - (windowStart + windowSteps.length);

  return [
    countStep(
      previousCount,
      previousCount > 0 ? previousCount - 1 : undefined,
      'count-previous'
    ),
    ...mapSteps(windowSteps, windowStart),
    countStep(
      remainingCount,
      remainingCount > 0 ? windowStart + windowSteps.length : undefined,
      'count-remaining'
    ),
  ];
};

const computeBubbleSize = (containerWidth: number, displayCount: number): number => {
  if (containerWidth <= 0 || displayCount <= 0) {
    return DEFAULT_BUBBLE_SIZE;
  }
  const totalDividerWidth = Math.max(0, displayCount - 1) * DIVIDER_SIZE;
  const availableForBubbles = containerWidth - CONTENT_PADDING - totalDividerWidth;
  const size = Math.floor(availableForBubbles / displayCount);
  return Math.min(MAX_BUBBLE_SIZE, Math.max(MIN_BUBBLE_SIZE, size));
};

const StepBubble = ({ step, isLast, size, onStepClick }: StepBubbleProps) => {
  const isCountBubble = Boolean(step.isCount);
  const isActiveStep = Boolean(step.isActive);
  const hasImage = Boolean(step.image);

  const activeSize = size + 2;
  const boxSize = !isCountBubble && (isActiveStep || hasImage) ? activeSize : size;

  return (
    <Fragment>
      <View style={{ width: boxSize, height: boxSize }}>
        <Pressable
          disabled={step.disabled || (isCountBubble && step.stepIndex == null)}
          style={[
            styles.itemButton,
            isActiveStep ? styles.activeItemButton : null,
            hasImage ? styles.imageItemButton : null,
            isCountBubble ? styles.countItemButton : null,
          ]}
          borderRadius={50}
          hoveredStyle={isCountBubble ? styles.countItemHover : undefined}
          onPress={() => {
            if (step.stepIndex != null) {
              onStepClick?.(step.stepIndex);
            }
          }}
        >
          {isCountBubble ? (
            <Text.Ui
              size="sm"
              weight="semibold"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
              style={styles.countLabel}
            >
              {step.countLabel}
            </Text.Ui>
          ) : hasImage ? (
            <View style={styles.imageWrapper}>
              <Image source={{ uri: step.image }} contentFit="contain" style={styles.image} />
              {isActiveStep && (
                <View style={styles.editBadge}>
                  <IconEdit size={14} color={tokens.colors.$blackOpacity55} />
                </View>
              )}
            </View>
          ) : isActiveStep ? (
            <IconEdit size={20} color={tokens.colors.$blackOpacity30} />
          ) : null}
        </Pressable>
      </View>
      {!isLast && <View style={styles.divider} />}
    </Fragment>
  );
};

export function ItemStepper(props: ItemStepperProps) {
  const { steps, onStepClick, ...scrollViewProps } = props;
  const { onLayout, onScroll, scrollEventThrottle } = scrollViewProps;
  const [containerWidth, setContainerWidth] = useState(0);

  const maxVisibleSteps = useMemo(
    () => getMaxVisibleSteps(containerWidth),
    [containerWidth]
  );

  const displaySteps = useMemo(
    () => buildDisplaySteps(steps, maxVisibleSteps),
    [steps, maxVisibleSteps]
  );

  const bubbleSize = useMemo(
    () => computeBubbleSize(containerWidth, displaySteps.length),
    [containerWidth, displaySteps.length]
  );

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      setContainerWidth(event.nativeEvent.layout.width);
      onLayout?.(event);
    },
    [onLayout]
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll?.(event);
    },
    [onScroll]
  );

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={scrollEventThrottle ?? 16}
      {...scrollViewProps}
      style={[styles.scroller, scrollViewProps.style]}
      contentContainerStyle={[styles.scrollerContent, scrollViewProps.contentContainerStyle]}
      onLayout={handleLayout}
      onScroll={handleScroll}
    >
      {displaySteps.map((step, index) => (
        <StepBubble
          key={step.key}
          step={step}
          size={bubbleSize}
          isLast={index === displaySteps.length - 1}
          onStepClick={onStepClick}
        />
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroller: {
    flexShrink: 1,
    flexGrow: 1,
  },
  scrollerContent: {
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemButton: {
    height: '100%',
    width: '100%',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: tokens.colors.$blackOpacity30,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
    position: 'absolute',
  },
  countItemButton: {
    backgroundColor: tokens.colors.$white,
  },
  countItemHover: {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  countLabel: {
    color: tokens.colors.$blackOpacity55,
  },
  activeItemButton: {
    borderWidth: 2,
  },
  imageItemButton: {
    borderWidth: 2,
    borderColor: tokens.colors.$success,
  },
  image: {
    aspectRatio: 1,
    height: '100%',
    overflow: 'visible',
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editBadge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: tokens.colors.$white,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    height: 2,
    width: DIVIDER_SIZE,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
});
